import { useCallback, useEffect, useState } from 'react'
import { CloseButton, Menu, MenuButton, MenuItem, MenuItems, Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import {
  AdjustmentsHorizontalIcon,
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClipboardDocumentIcon,
  EllipsisVerticalIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { useAuth } from '../providers/auth/useAuth'
import {
  AdManagementService,
  type GeneratedAdResponse,
  type MetaAdset,
  type MetaCampaign,
  type MetaConnectionRead,
} from '../client'
import AccountPageSelector from '../components/ads/AccountPageSelector'
import AdChatReview from '../components/ads/AdChatReview'
import AdsTable from '../components/ads/AdsTable'
import AdsTableSkeleton from '../components/ads/AdsTableSkeleton'
import AuditLogDialog from '../components/ads/AuditLogDialog'
import ConnectionSwitcher from '../components/ads/ConnectionSwitcher'
import DraftAdCard from '../components/ads/DraftAdCard'
import FilterSelect from '../components/ads/FilterSelect'
import GenerateAdsDialog, { type GenerationStarted } from '../components/ads/GenerateAdsDialog'
import GenerateVideoDialog from '../components/video/GenerateVideoDialog'
import GeneratedAdFlow from '../components/ads/GeneratedAdFlow'
import MetaIcon from '../components/ads/MetaIcon'
import Modal from '../components/ui/Modal'
import { AVAILABLE_COLUMNS, DEFAULT_COLUMNS } from '../lib/adColumns'
import { apiErrorMessage } from '../lib/api'
import { openMetaAuthPopup } from '../lib/metaAuth'
import posthog from '../lib/posthog'

const COLUMNS_KEY = 'metaAds.selectedColumns'
const PICKER = 'inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-300 bg-stone-100 px-3 py-1.5 text-sm font-medium text-gray-700 transition hover:bg-stone-200 disabled:opacity-60'

function DraftsSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
      {[0, 1, 2, 3].map((card) => (
        <div key={card} className="overflow-hidden rounded-xl border border-gray-200 bg-stone-100">
          <div className="aspect-square animate-pulse bg-gray-200" />
          <div className="space-y-2 p-3">
            <div className="h-3 w-3/4 animate-pulse rounded bg-gray-200" />
            <div className="h-2.5 w-full animate-pulse rounded bg-gray-200" />
            <div className="h-6 w-full animate-pulse rounded-lg bg-gray-200" />
          </div>
        </div>
      ))}
    </div>
  )
}

function needsAdAccountSetup(connection: MetaConnectionRead | null) {
  return Boolean(connection && (!connection.last_used_ad_account_id || !connection.last_used_page_id))
}

function storedColumns() {
  try {
    const raw = localStorage.getItem(COLUMNS_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : null
    return Array.isArray(parsed) && parsed.every((key) => typeof key === 'string') ? (parsed as string[]) : null
  } catch {
    return null
  }
}

export default function MyAds() {
  const { brand } = useAuth()
  const brandId = brand?.id
  const [tab, setTab] = useState<'ads' | 'drafts'>('ads')
  const [connection, setConnection] = useState<MetaConnectionRead | null>(null)
  const [drafts, setDrafts] = useState<GeneratedAdResponse[]>([])
  const [credits, setCredits] = useState({ consumed: 0, limit: 0, remaining: 0 })
  const [loading, setLoading] = useState(true)
  const [loadingDrafts, setLoadingDrafts] = useState(true)
  const [connecting, setConnecting] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [generatingVideo, setGeneratingVideo] = useState(false)
  const [review, setReview] = useState<GenerationStarted | null>(null)
  const [publishing, setPublishing] = useState<GeneratedAdResponse[] | null>(null)
  const [campaigns, setCampaigns] = useState<MetaCampaign[]>([])
  const [adsets, setAdsets] = useState<MetaAdset[]>([])
  const [campaignId, setCampaignId] = useState('')
  const [adsetId, setAdsetId] = useState('')
  const [columns, setColumns] = useState<string[]>(() => storedColumns() ?? DEFAULT_COLUMNS)
  const [openDialog, setOpenDialog] = useState<'logs' | 'connections' | 'defaults' | null>(null)
  const [adlovinOnly, setAdlovinOnly] = useState(false)
  const [error, setError] = useState('')

  const fetchDrafts = useCallback(async (brand: number) => {
    const [doc, usage] = await Promise.all([
      AdManagementService.getAdGenerationDoc(brand),
      AdManagementService.credits(brand),
    ])
    return { ads: (doc?.generated_ads ?? []).filter(({ image_url }) => Boolean(image_url)), usage }
  }, [])

  const applyDrafts = useCallback(({ ads, usage }: { ads: GeneratedAdResponse[]; usage: typeof credits }) => {
    setDrafts(ads)
    setCredits(usage)
  }, [])

  useEffect(() => {
    if (!brandId) return

    AdManagementService.getConnection(brandId)
      .then((loaded) => {
        setConnection(loaded)
        if (needsAdAccountSetup(loaded)) setOpenDialog('defaults')
      })
      .catch((err: unknown) => setError(apiErrorMessage(err)))
      .finally(() => setLoading(false))

    fetchDrafts(brandId)
      .then(applyDrafts)
      .catch(() => undefined)
      .finally(() => setLoadingDrafts(false))
  }, [brandId, fetchDrafts, applyDrafts])

  useEffect(() => {
    const accountId = connection?.last_used_ad_account_id
    if (!brandId || !accountId) return

    AdManagementService.getCampaigns(brandId, accountId)
      .then(setCampaigns)
      .catch(() => undefined)
  }, [brandId, connection?.last_used_ad_account_id])

  useEffect(() => {
    if (!brandId || !campaignId) return

    AdManagementService.getAdsets(campaignId, brandId)
      .then(setAdsets)
      .catch(() => undefined)
  }, [brandId, campaignId])

  function toggleColumn(key: string) {
    setColumns((current) => {
      const next = current.includes(key) ? current.filter((item) => item !== key) : [...current, key]
      try {
        localStorage.setItem(COLUMNS_KEY, JSON.stringify(next))
      } catch {
        // ignored
      }
      return next
    })
  }

  async function disconnect() {
    if (!brandId || !connection) return

    try {
      await AdManagementService.disconnect({ brand_id: brandId, connection_id: connection.id })
      setConnection(null)
    } catch (err) {
      setError(apiErrorMessage(err))
    }
  }

  /** A different ad account means different campaigns, ad sets and ads. */
  function applyConnection(updated: MetaConnectionRead | null) {
    setOpenDialog(needsAdAccountSetup(updated) ? 'defaults' : null)
    setCampaignId('')
    setAdsetId('')
    setCampaigns([])
    setAdsets([])
    setConnection(updated)
  }

  async function refreshConnection(brand: number) {
    applyConnection(await AdManagementService.getConnection(brand))
  }

  async function connect() {
    if (!brandId) return

    setConnecting(true)
    setError('')
    posthog.capture('meta_connection_started')

    try {
      await openMetaAuthPopup(brandId)
      const connected = await AdManagementService.getConnection(brandId)
      setConnection(connected)
      if (!connected) setError('Meta did not finish connecting. Please try again.')
      else {
        posthog.capture('meta_connection_completed')
        if (needsAdAccountSetup(connected)) setOpenDialog('defaults')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : apiErrorMessage(err))
    } finally {
      setConnecting(false)
    }
  }

  return (
    <div className="flex flex-1 flex-col gap-4 p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
        <div className="min-w-0 flex-1">
          <h1 className="font-heading text-xl font-semibold text-slate-900">My ads</h1>
          <p className="mt-0.5 text-sm text-slate-500">Everything you've generated and published.</p>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-3">
          {tab === 'drafts' ? (
            <button
              type="button"
              onClick={() => setTab('ads')}
              className="flex items-center gap-1 text-sm text-gray-500 transition hover:text-gray-800"
            >
              <ArrowLeftIcon className="size-3.5" /> Back
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setTab('drafts')}
              className="inline-flex items-center rounded-[10px] border border-stone-200 bg-stone-100 px-4 py-2 text-sm font-medium text-stone-700 shadow-sm transition hover:bg-stone-200"
            >
              <ClipboardDocumentIcon className="mr-2 size-5 text-stone-400" />
              Draft Ads
            </button>
          )}

          <Menu as="div" className="relative inline-block text-left">
            <MenuButton className="inline-flex w-full items-center justify-center rounded-[10px] bg-linear-to-b from-stone-700 to-stone-950 px-4 py-2 text-sm font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_1px_2px_rgba(28,25,23,0.2)] transition-opacity hover:opacity-90 focus:outline-none">
              <span className="grow">Create Ads</span>
              <ChevronDownIcon className="-mr-1 ml-2 size-5 text-stone-300" />
            </MenuButton>
            <MenuItems
              anchor={{ to: 'bottom end', gap: 8 }}
              transition
              className="z-50 w-72 origin-top-right divide-y divide-stone-100 rounded-xl bg-white shadow-lg ring-1 ring-black/5 transition duration-100 ease-out focus:outline-none data-closed:scale-95 data-closed:opacity-0"
            >
              <div className="px-1 py-1">
                <MenuItem>
                  <button
                    type="button"
                    onClick={() => setGenerating(true)}
                    className="group flex w-full items-center rounded-lg px-2 py-2 text-sm text-stone-900 data-focus:bg-stone-100"
                  >
                    <span className="flex flex-col text-left">
                      <span className="font-semibold">AI Static Ads</span>
                      <span className="text-stone-500">AI-powered ad generation.</span>
                    </span>
                  </button>
                </MenuItem>
                <MenuItem>
                  <button
                    type="button"
                    onClick={() => setGeneratingVideo(true)}
                    className="group flex w-full items-center rounded-lg px-2 py-2 text-sm text-stone-900 data-focus:bg-stone-100"
                  >
                    <span className="flex flex-col text-left">
                      <span className="font-semibold">AI Video Ads</span>
                      <span className="text-stone-500">Generate clips, then edit them in Studio.</span>
                    </span>
                  </button>
                </MenuItem>
              </div>
            </MenuItems>
          </Menu>
        </div>
      </div>

      {error && (
        <p role="alert" className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-800">
          {error}
        </p>
      )}

      {tab === 'drafts' ? (
        <div className="rounded-2xl border border-slate-100 p-4">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900">Draft Ads</h3>
            <span className="text-xs text-gray-400">
              {credits.consumed} / {credits.limit} credits used
            </span>
          </div>

          {loadingDrafts ? (
            <DraftsSkeleton />
          ) : drafts.length > 0 ? (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              {drafts.map((ad, index) => (
                <DraftAdCard
                  key={ad.image_url ?? index}
                  ad={ad}
                  onCreateAd={() => setPublishing([ad])}
                  onEditImage={() =>
                    setReview({
                      ads: [ad],
                      adIndices: [index],
                      creditsRemaining: credits.remaining,
                      creditsLimit: credits.limit,
                    })
                  }
                />
              ))}
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-gray-400">
              No draft ads yet. Use <span className="font-medium text-gray-600">Create Ads</span> to generate
              AI-powered ads.
            </p>
          )}
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-100 p-6">
          {loading ? (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <span className="h-5 w-40 animate-pulse rounded bg-gray-200" />
                <span className="h-5 w-32 animate-pulse rounded bg-gray-200" />
              </div>
              <AdsTableSkeleton />
            </div>
          ) : connection ? (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
                  <span className="flex max-w-full items-center gap-2 text-sm whitespace-nowrap text-gray-700">
                    Logged in as
                    <MetaIcon className="size-4 shrink-0 text-[#1877F2]" />
                    <strong className="truncate">{connection.meta_user_name ?? connection.meta_user_id}</strong>
                  </span>
                  {connection.last_used_ad_account_name && (
                    <span className="rounded bg-stone-200 px-2 py-0.5 text-xs">
                      <span className="font-medium text-gray-600">Ad Account: </span>
                      <span className="text-gray-500">{connection.last_used_ad_account_name}</span>
                    </span>
                  )}
                  {connection.last_used_page_name && (
                    <span className="rounded bg-stone-200 px-2 py-0.5 text-xs">
                      <span className="font-medium text-gray-600">Facebook Page: </span>
                      <span className="text-gray-500">{connection.last_used_page_name}</span>
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => setOpenDialog('defaults')}
                    className="text-xs text-indigo-600 hover:underline"
                  >
                    Switch
                  </button>
                </div>

                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setAdlovinOnly((current) => !current)}
                    className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium whitespace-nowrap transition ${
                      adlovinOnly
                        ? 'border-indigo-600 bg-indigo-100 text-indigo-600'
                        : 'border-gray-300 bg-stone-100 text-gray-700 hover:bg-stone-200'
                    }`}
                  >
                    <span
                      className={`flex h-3.5 w-6 shrink-0 items-center rounded-full px-0.5 transition-colors ${
                        adlovinOnly ? 'bg-indigo-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`size-2.5 rounded-full bg-white shadow transition-transform ${
                          adlovinOnly ? 'translate-x-2.5' : ''
                        }`}
                      />
                    </span>
                    Adlovin Ads
                  </button>

                  <FilterSelect
                    allLabel="All campaigns"
                    options={campaigns}
                    value={campaignId}
                    labelWidth="max-w-[140px]"
                    onChange={(id) => {
                      setCampaignId(id)
                      setAdsetId('')
                    }}
                  />

                  {campaignId && (
                    <>
                      <ChevronRightIcon className="size-3.5 shrink-0 text-gray-400" />
                      <FilterSelect
                        allLabel="All ad sets"
                        options={adsets}
                        value={adsetId}
                        labelWidth="max-w-[120px]"
                        onChange={setAdsetId}
                      />
                      <button
                        type="button"
                        aria-label="Clear filter"
                        title="Clear filter"
                        onClick={() => {
                          setCampaignId('')
                          setAdsetId('')
                        }}
                        className="rounded p-1 text-gray-400 transition hover:text-gray-600"
                      >
                        <XMarkIcon className="size-4" />
                      </button>
                    </>
                  )}

                  <Popover className="relative">
                    <PopoverButton className={PICKER}>
                      <AdjustmentsHorizontalIcon className="size-4" />
                      Columns ({columns.length})
                    </PopoverButton>
                    <PopoverPanel
                      anchor={{ to: 'bottom end', gap: 4 }}
                      className="z-20 w-64 overflow-y-auto [--anchor-max-height:20rem] rounded-lg border border-gray-200 bg-white py-2 shadow-lg"
                    >
                      <p className="mb-1 border-b border-gray-100 px-3 pb-2 text-xs text-gray-400">Toggle columns</p>
                      {AVAILABLE_COLUMNS.map(({ key, label }) => (
                        <label key={key} className="flex cursor-pointer items-center gap-2 px-3 py-1.5 hover:bg-gray-50">
                          <input
                            type="checkbox"
                            checked={columns.includes(key)}
                            onChange={() => toggleColumn(key)}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="text-sm text-gray-700">{label}</span>
                        </label>
                      ))}
                    </PopoverPanel>
                  </Popover>


                  <Popover className="relative">
                    <PopoverButton className="flex items-center rounded-full p-1 outline-0 hover:bg-stone-200">
                      <EllipsisVerticalIcon className="size-5 text-slate-600" />
                    </PopoverButton>
                    <PopoverPanel
                      anchor={{ to: 'bottom end', gap: 8 }}
                      className="z-20 rounded bg-white py-1 whitespace-nowrap drop-shadow"
                    >
                      <CloseButton
                        onClick={() => setOpenDialog('logs')}
                        className="block w-full px-4 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100"
                      >
                        See Logs
                      </CloseButton>
                      <CloseButton
                        onClick={() => setOpenDialog('connections')}
                        className="block w-full px-4 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Switch Account
                      </CloseButton>
                      <CloseButton
                        onClick={() => void disconnect()}
                        className="block w-full px-4 py-1.5 text-left text-sm text-red-600 hover:bg-red-50"
                      >
                        Disconnect
                      </CloseButton>
                    </PopoverPanel>
                  </Popover>
                </div>
              </div>

              {needsAdAccountSetup(connection) ? (
                <div className="py-12 text-center">
                  <button
                    type="button"
                    onClick={() => setOpenDialog('defaults')}
                    className="inline-flex items-center gap-2 rounded-[10px] bg-linear-to-b from-stone-700 to-stone-950 px-4 py-2 text-sm font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_1px_2px_rgba(28,25,23,0.2)] transition-opacity hover:opacity-90"
                  >
                    <MetaIcon className="size-4" />
                    Set up your ad account
                  </button>
                </div>
              ) : (
                <AdsTable
                  key={`${connection.id}|${connection.last_used_ad_account_id}|${campaignId}|${adsetId}`}
                  brandId={brandId!}
                  adAccountId={connection.last_used_ad_account_id}
                  campaignId={campaignId}
                  adsetId={adsetId}
                  adlovinOnly={adlovinOnly}
                  columns={columns}
                />
              )}
            </div>
          ) : (
            <div className="py-12 text-center">
              <MetaIcon className="mx-auto mb-4 size-12 text-[#1877F2]" />
              <h3 className="mb-2 text-lg font-semibold text-stone-900">Connect your Meta account</h3>
              <p className="mx-auto mb-6 max-w-md text-sm text-stone-500">
                Link your Meta Business account to publish generated ads to Facebook and Instagram.
              </p>
              <button
                type="button"
                disabled={connecting || !brandId}
                onClick={() => void connect()}
                className="inline-flex items-center gap-2 rounded-[10px] bg-linear-to-b from-stone-700 to-stone-950 px-4 py-2 text-sm font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_1px_2px_rgba(28,25,23,0.2)] transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                <MetaIcon className="size-4" />
                {connecting ? 'Waiting for Meta…' : 'Connect Meta Account'}
              </button>
            </div>
          )}
        </div>
      )}

      {openDialog === 'logs' && brandId && <AuditLogDialog brandId={brandId} onClose={() => setOpenDialog(null)} />}

      {openDialog === 'connections' && brandId && (
        <ConnectionSwitcher
          brandId={brandId}
          onClose={() => setOpenDialog(null)}
          onSwitched={() => void refreshConnection(brandId).catch((err: unknown) => setError(apiErrorMessage(err)))}
        />
      )}

      {openDialog === 'defaults' && brandId && (
        <Modal title="Change Ad Account & Page" onClose={() => setOpenDialog(null)}>
          <AccountPageSelector
            brandId={brandId}
            initialAdAccountId={connection?.last_used_ad_account_id}
            initialPageId={connection?.last_used_page_id}
            onBack={() => setOpenDialog(null)}
            onSaved={applyConnection}
          />
        </Modal>
      )}

      {generatingVideo && brandId && (
        <GenerateVideoDialog brandId={brandId} onClose={() => setGeneratingVideo(false)} />
      )}

      {generating && brandId && (
        <GenerateAdsDialog
          brandId={brandId}
          onClose={() => setGenerating(false)}
          onGenerated={(payload) => {
            setGenerating(false)
            setReview(payload)
          }}
        />
      )}

      {review && brandId && (
        <AdChatReview
          brandId={brandId}
          ads={review.ads}
          adIndices={review.adIndices}
          creditsRemaining={review.creditsRemaining}
          creditsLimit={review.creditsLimit}
          onClose={() => setReview(null)}
          onContinue={(updated) => {
            setReview(null)
            setPublishing(updated)
            void fetchDrafts(brandId).then(applyDrafts).catch(() => undefined)
          }}
        />
      )}

      {publishing && brandId && (
        <GeneratedAdFlow
          brandId={brandId}
          ads={publishing}
          onClose={() => setPublishing(null)}
          onPromoted={() => {
            setPublishing(null)
            setTab('ads')
          }}
        />
      )}
    </div>
  )
}
